import * as React from 'react'

import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import CloseIcon from '@mui/icons-material/Close'

import { useSnackbar } from '../Snackbar'
import {
    generateShareLinkNode,
    attachShareDialogHandlers,
    detachShareDialogHandlers,
    trackEvent,
    emitEvent,
} from '../LeftMenuMigration/legacyBridge'
import { downloadGraphPng as snapshotGraphPng } from '../LeftMenuMigration/canvasSnapshot'
import useCompareMode from '../CompareViewPanel/useCompareMode'
import { useBootstrapStore } from '../../stores/useBootstrapStore'
import {
    useContextsStore,
    selectIsPublicView,
} from '../../stores/useContextsStore'

const showSnackbarSelector = s => s.showSnackbar

/**
 * Snapshot the live 3D graph canvas to a PNG and download it. Wraps the
 * shared `canvasSnapshot.downloadGraphPng` to add this dialog's analytics
 * events and surface a snackbar on canvas-not-ready.
 */
function shareGraphPng(showSnackbar) {
    const ok = snapshotGraphPng()
    if (!ok) {
        showSnackbar(
            'error',
            'Could not capture the graph image — graph canvas not ready.'
        )
        return
    }
    emitEvent('onGraphAction', { action: 'download', data: 'png', modal: 'sharepng' })
    trackEvent('share_png')
}

const TWEET_URL =
    'https://twitter.com/intent/tweet?text=Text%20network%20graph%20made%20with%20%23infranodus&url=https%3A%2F%2Finfranodus.com'

/**
 * Share / Embed dialog.
 *
 * Two sections:
 *
 *   1. "Share Graph Image" — PNG snapshot of the graph canvas plus a
 *      Tweet button. Native React.
 *
 *   2. "Share Interactive Text Graph" — wraps the jQuery DOM returned
 *      by `window.graphShareConstructor.generateShareLink()`. That form
 *      contains the embed URL, iframe code, the three "hide …" embed
 *      toggles (statement controls / AI functionality / project notes),
 *      and privacy toggle. Mounted via a callback ref so MUI Dialog's
 *      portal timing isn't a problem.
 */
export default function ShareDialog({ open, onClose }) {
    const showSnackbar = useSnackbar(showSnackbarSelector)
    // Compare/synthesis/search are transient multi-context views, not a
    // saveable context, so there's no interactive graph to embed/share.
    const { isCompareMode } = useCompareMode()
    const isSynthesisMode = useBootstrapStore(s => s.synthesisType) != null
    const isSearchMode = useBootstrapStore(s => s.viewType) === 'search'
    // The interactive-share section's privacy toggle and link generator are
    // owner-only affordances; non-owners would see a UI that can't act.
    const isPublic = useContextsStore(selectIsPublicView)
    const hideInteractiveShare =
        isSynthesisMode || isSearchMode || isPublic

    // Clear the embed-preview tints applied by `previewEmbedHide` whenever
    // the dialog closes, even if the user never touched a checkbox
    // (e.g. they reopen, leave a box ticked, dismiss without copying).
    React.useEffect(() => {
        if (!open) {
            detachShareDialogHandlers()
        }
    }, [open])

    const containerRef = React.useCallback(
        node => {
            if (!node || !open) return
            const legacyNode = generateShareLinkNode()
            if (!legacyNode) {
                showSnackbar(
                    'error',
                    'Share dialog is not ready yet. Reload the page and try again.'
                )
                return
            }
            node.innerHTML = ''
            const dom = legacyNode[0] || legacyNode
            try {
                node.appendChild(dom)
            } catch (e) {
                console.warn('[ShareDialog] failed to mount legacy form:', e)
                showSnackbar('error', "Couldn't open the share dialog.")
                return
            }
            attachShareDialogHandlers()
        },
        [open, showSnackbar]
    )

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ pr: 6 }}>
                Share or Embed Graph
                <IconButton
                    aria-label="close-share-dialog"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme => theme.palette.text.secondary,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Share Graph Image
                </Typography>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}
                >
                    Share a non-interactive image of the graph only, no text.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => shareGraphPng(showSnackbar)}
                        sx={{ textTransform: 'none' }}
                        data-testid="share-png-download"
                    >
                        Download Image
                    </Button>
                    {/* Tweet: rendered as a Button (not an <a>) because it
                        does TWO things — open the tweet intent AND trigger
                        a PNG download. As a link the second action would be
                        invisible to screen readers; as a Button + handler
                        the dual effect is honest, and the visible hint
                        below describes it. Mirrors legacy
                        `Graph.ejs:3265-3266` (`#tweetpng` synthesised a
                        click on `#sharepng`). */}
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                            shareGraphPng(showSnackbar)
                            trackEvent('share_tweet')
                            window.open(
                                TWEET_URL,
                                '_blank',
                                'noopener,noreferrer'
                            )
                        }}
                        aria-describedby="share-png-tweet-hint"
                        sx={{ textTransform: 'none' }}
                        data-testid="share-png-tweet"
                    >
                        Tweet
                    </Button>
                </Stack>
                <Typography
                    id="share-png-tweet-hint"
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.75 }}
                >
                    Tweet also downloads the image so you can attach it.
                </Typography>

                {!hideInteractiveShare && (
                    <>
                        <Divider sx={{ my: 2 }} />

                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 0.5 }}
                        >
                            Share Interactive Text Graph
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mb: 1 }}
                        >
                            Your user will see the panels and graph layers
                            that are currently shown on the graph. To
                            control, modify the view and open this dialogue
                            again.
                        </Typography>
                        {isCompareMode && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Making this comparison public also makes each graph it contains public at its own URL.
                            </Typography>
                        )}
                        <div
                            data-testid="share-dialog-content"
                            ref={containerRef}
                        />
                    </>
                )}
            </DialogContent>
        </Dialog>
    )
}
